// =============================================================================
// features/meu-whatsapp/WhatsAppStatusPill.tsx — Selo "WhatsApp desconectado"
// na topbar (F57-S02). Forma minimizada do aviso da guarda de conexão.
//
// Fica enquanto durar a desconexão (não tem "fechar") e reabre o modal com o
// passo a passo. Mora DENTRO da topbar fixa: não cobre o campo de digitar no
// celular (iPhone inclusive) e não muda a altura de nenhuma tela.
//
// Celular: só o ícone, alvo 44×44, com `aria-label`. A partir de `sm`, o texto.
// Hover: Lift (doc 18 §8). Pulso do ponto respeita `prefers-reduced-motion`.
// =============================================================================

import * as React from 'react';
import { useLocation } from 'react-router-dom';

import { cn } from '../../lib/cn';

import { shouldShowStatusPill } from './connectionGuard';
import {
  useConnectionGuardEnabled,
  useConnectionGuardStore,
  useWhatsAppConnection,
} from './useWhatsAppConnection';

function PillContent(): React.JSX.Element | null {
  const { pathname } = useLocation();
  const { data } = useWhatsAppConnection(true);
  const openModal = useConnectionGuardStore((s) => s.openModal);

  if (!shouldShowStatusPill(true, data ?? null, pathname)) return null;

  return (
    <button
      type="button"
      onClick={openModal}
      aria-label="Seu WhatsApp está desconectado. Ver como conectar"
      className={cn(
        'inline-flex items-center justify-center gap-2 shrink-0',
        'h-11 w-11 sm:h-9 sm:w-auto sm:px-3 rounded-full',
        'font-sans text-xs font-semibold',
        'transition-[transform,box-shadow] duration-fast ease-out',
        'hover:-translate-y-px hover:[box-shadow:var(--elev-3)] active:translate-y-0',
        'focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-danger/25',
        'motion-reduce:transition-none motion-reduce:hover:translate-y-0',
      )}
      style={{
        background: 'var(--danger-bg)',
        color: 'var(--danger)',
        border: '1px solid color-mix(in srgb, var(--danger) 35%, transparent)',
        boxShadow: 'var(--elev-1)',
      }}
    >
      <span className="relative flex h-2 w-2" aria-hidden="true">
        <span
          className="absolute inline-flex h-full w-full rounded-full opacity-60 animate-ping motion-reduce:animate-none"
          style={{ background: 'var(--danger)' }}
        />
        <span
          className="relative inline-flex h-2 w-2 rounded-full"
          style={{ background: 'var(--danger)' }}
        />
      </span>
      <span className="hidden sm:inline whitespace-nowrap">WhatsApp desconectado</span>
    </button>
  );
}

/** Sem a flag não monta nada (nem consulta). */
export function WhatsAppStatusPill(): React.JSX.Element | null {
  const enabled = useConnectionGuardEnabled();
  return enabled ? <PillContent /> : null;
}
